import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';

export interface AddressJson {
  street?: string;
  city?: string;
  state?: string;
  postal?: string;
  country?: string;
}

@Component({
  selector: 'app-address',
  standalone: true,
  imports: [CommonModule, FormsModule, InputTextModule],
  template: `
    <div class="address-component">
      <!-- Display Mode -->
      <div *ngIf="!editMode" class="address-display">
        <div *ngIf="hasAddress()" class="address-lines">
          <div *ngIf="address?.street" class="address-line">
            {{ address?.street }}
          </div>
          <div class="address-line-group">
            <span *ngIf="address?.city">{{ address?.city }}</span>
            <span *ngIf="address?.city && address?.state">, </span>
            <span *ngIf="address?.state">{{ address?.state }}</span>
            <span *ngIf="address?.postal && (address?.city || address?.state)">
              &nbsp;
            </span>
            <span *ngIf="address?.postal">{{ address?.postal }}</span>
          </div>
          <div *ngIf="address?.country" class="address-line">
            {{ address?.country }}
          </div>
        </div>
        <div *ngIf="!hasAddress()" class="no-address">No address provided</div>
      </div>

      <!-- Edit Mode -->
      <div *ngIf="editMode" class="address-edit">
        <div class="address-field">
          <label for="street">Street</label>
          <input
            id="street"
            type="text"
            pInputText
            [(ngModel)]="editingAddress.street"
            (ngModelChange)="onAddressChange()"
            placeholder="Street address"
          />
        </div>
        <div class="address-field">
          <label for="city">City</label>
          <input
            id="city"
            type="text"
            pInputText
            [(ngModel)]="editingAddress.city"
            (ngModelChange)="onAddressChange()"
            placeholder="City"
          />
        </div>
        <div class="address-field">
          <label for="state">State/Province</label>
          <input
            id="state"
            type="text"
            pInputText
            [(ngModel)]="editingAddress.state"
            (ngModelChange)="onAddressChange()"
            placeholder="State or Province"
          />
        </div>
        <div class="address-field">
          <label for="postal">Postal Code</label>
          <input
            id="postal"
            type="text"
            pInputText
            [(ngModel)]="editingAddress.postal"
            (ngModelChange)="onAddressChange()"
            placeholder="Postal code"
          />
        </div>
        <div class="address-field">
          <label for="country">Country</label>
          <input
            id="country"
            type="text"
            pInputText
            [(ngModel)]="editingAddress.country"
            (ngModelChange)="onAddressChange()"
            placeholder="Country"
          />
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .address-component {
        width: 100%;
      }

      .address-display {
        padding: 0.75rem;
        background: var(--surface-card);
        border: 1px solid var(--surface-border);
        border-radius: 6px;
      }

      .address-lines {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }

      .address-line {
        line-height: 1.5;
        color: var(--text-color);
      }

      .address-line-group {
        line-height: 1.5;
        color: var(--text-color);
      }

      .no-address {
        color: var(--text-color-secondary);
        font-style: italic;
      }

      .address-edit {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .address-field {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .address-field label {
        font-weight: 500;
        color: var(--text-color);
        font-size: 0.875rem;
      }

      .address-field input {
        width: 100%;
      }
    `,
  ],
})
export class AddressComponent implements OnInit, OnChanges {
  @Input() address: AddressJson | null = null;
  @Input() editMode: boolean = false;

  @Output() addressChange = new EventEmitter<AddressJson | null>();

  editingAddress: AddressJson = {};

  ngOnInit() {
    this.initializeAddress();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['address'] || changes['editMode']) {
      this.initializeAddress();
    }
  }

  initializeAddress() {
    if (this.address && typeof this.address === 'object') {
      this.editingAddress = {
        street: this.address.street || '',
        city: this.address.city || '',
        state: this.address.state || '',
        postal: this.address.postal || '',
        country: this.address.country || '',
      };
    } else {
      this.editingAddress = {
        street: '',
        city: '',
        state: '',
        postal: '',
        country: '',
      };
    }
  }

  hasAddress(): boolean {
    if (!this.address || typeof this.address !== 'object') {
      return false;
    }
    return !!(
      this.address.street ||
      this.address.city ||
      this.address.state ||
      this.address.postal ||
      this.address.country
    );
  }

  onAddressChange() {
    // Create a clean address object with only defined values
    const cleanAddress: AddressJson = {};

    if (this.editingAddress.street?.trim()) {
      cleanAddress.street = this.editingAddress.street.trim();
    }
    if (this.editingAddress.city?.trim()) {
      cleanAddress.city = this.editingAddress.city.trim();
    }
    if (this.editingAddress.state?.trim()) {
      cleanAddress.state = this.editingAddress.state.trim();
    }
    if (this.editingAddress.postal?.trim()) {
      cleanAddress.postal = this.editingAddress.postal.trim();
    }
    if (this.editingAddress.country?.trim()) {
      cleanAddress.country = this.editingAddress.country.trim();
    }

    // Emit null if all fields are empty, otherwise emit the address object
    const hasAnyValue = Object.keys(cleanAddress).length > 0;
    this.addressChange.emit(hasAnyValue ? cleanAddress : null);
  }
}
